import { useState } from "react";
import PropTypes from "prop-types";
import { useMutation } from "@tanstack/react-query";
import {
  signolImagesRepositoryURL,
  UsernameTypes,
  DEFAULT_LANGUAGE,
} from "@src/constants/constants";
import { AppUtils } from "@src/utils";
import AuthHooks from "@src/context/hooks/authHooks";
import { authEndpoints } from "@src/services/endpoints";
import {
  Checkbox,
  ImageWithRetry,
  Button,
  FormInput,
} from "@src/components/atoms";
import Form from "./Form";
import { resetPasswordSchema } from "./formHelpers";

const envType = import.meta.env.VITE_REACT_APP_ENV;

const ResetPasswordForm = ({ token, onSuccess }) => {
  const [error, setError] = useState("");

  const resetPasswordMutation = useMutation({
    mutationKey: ["RESET_PASSWORD"],
    mutationFn: (data) =>
      authEndpoints.requestResetPassword({
        token,
        password: data.password,
        password2: data.password2,
      }),
    onSuccess: () => onSuccess?.(),
    onError: (error) => {
      console.error("Reset password error:", error);
      setError(
        error?.message ||
          "Failed to reset password. The link may have expired. Please try requesting a new reset link.",
      );
    },
  });

  const handleSubmit = (data) => {
    setError("");
    resetPasswordMutation.mutate(data);
  };
  return (
    <div className="bg-white rounded-2xl mx-8 overflow-clip w-full max-w-md xl:max-w-lg">
      <div className="mx-auto py-6 px-8 bg-white">
        <div className="mx-auto py-8 w-40">
          <ImageWithRetry
            src={`${signolImagesRepositoryURL}/SignolLogoPrimary.png`}
            alt="Signol logo"
          />
          {envType !== "production" && (
            <span className="relative bottom-3 float-right font-bold text-pink-400">
              {envType === "dev" ? "qa" : envType}
            </span>
          )}
        </div>

        <h3 className="text-2xl font-bold mb-2 text-center">
          Create New Password
        </h3>
        <p className="text-sm text-center text-gray-600 mb-6">
          Enter your new password below. Make sure it meets all the
          requirements.
        </p>

        <Form
          onSubmit={handleSubmit}
          schema={resetPasswordSchema}
          className="space-y-6"
        >
          {(methods) => (
            <>
              <FormInput
                label="New Password"
                name="password"
                type="password"
                placeholder="••••••••"
                error={methods.formState.errors.password?.message}
                showPasswordRules={true}
                textColor={"primary"}
                autoComplete="new-password"
              />

              <FormInput
                label="Confirm New Password"
                name="password2"
                type="password"
                placeholder="••••••••"
                error={methods.formState.errors.password2?.message}
                textColor="primary"
                autoComplete="new-password"
                validationMode="onChange"
              />

              <div className="flex justify-center">
                <Button
                  submit
                  loading={resetPasswordMutation.isPending}
                  disabled={resetPasswordMutation.isPending}
                  className="px-8"
                >
                  Reset Password
                </Button>
              </div>

              {error && (
                <div className="mt-4 text-xs italic text-red-500 text-center">
                  {error}
                </div>
              )}
            </>
          )}
        </Form>
      </div>
    </div>
  );
};

export const resetPasswordFormControls = {
  token: {
    propType: PropTypes.string.isRequired,
    description: "Password reset secret",
  },
  onSuccess: {
    propType: PropTypes.func.isRequired,
    description: "Form submission handler from parent",
    disable: true,
  },
};

ResetPasswordForm.propTypes = AppUtils.getPropTypeFromControls(
  resetPasswordFormControls,
);

export default ResetPasswordForm;
